import { useEffect, useRef, useState } from "react";
import { fetchGoogleAuthConfig } from "./api";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: string;
              theme?: string;
              size?: string;
              text?: string;
              shape?: string;
              width?: number;
              logo_alignment?: string;
            },
          ) => void;
        };
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Google Sign-In failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google Sign-In failed to load"));
    document.head.appendChild(script);
  });
}

type Props = {
  disabled?: boolean;
  onCredential: (idToken: string) => void;
  onError?: (message: string) => void;
};

/**
 * Official Google Identity Services button — credential is posted to auth-service.
 */
export function GoogleSignInButton({ disabled, onCredential, onError }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const [status, setStatus] = useState<"loading" | "ready" | "disabled" | "error">("loading");

  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (cancelled) return;
      setStatus((prev) => {
        if (prev !== "loading") return prev;
        onErrorRef.current?.("Google Sign-In timed out. Refresh and try again.");
        return "error";
      });
    }, 12000);

    async function mount() {
      try {
        const cfg = await fetchGoogleAuthConfig();
        if (cancelled) return;
        if (!cfg.enabled || !cfg.client_id) {
          setStatus("disabled");
          return;
        }
        await loadGisScript();
        if (cancelled || !hostRef.current || !window.google?.accounts?.id) return;

        hostRef.current.innerHTML = "";
        window.google.accounts.id.initialize({
          client_id: cfg.client_id,
          callback: (response) => {
            if (response?.credential) onCredentialRef.current(response.credential);
            else onErrorRef.current?.("Google did not return a credential.");
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        const width = Math.min(360, Math.max(280, hostRef.current.clientWidth || 320));
        window.google.accounts.id.renderButton(hostRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width,
          logo_alignment: "left",
        });
        setStatus("ready");
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          onErrorRef.current?.(err instanceof Error ? err.message : "Google Sign-In unavailable.");
        }
      }
    }

    void mount();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <div className={`pg-google-signin${disabled ? " is-disabled" : ""}`} aria-busy={status === "loading"}>
      {status === "loading" ? <p className="pg-muted pg-google-signin-hint">Loading Google Sign-In…</p> : null}
      {status === "disabled" ? (
        <p className="pg-muted pg-google-signin-hint">Google Sign-In is not configured on this server.</p>
      ) : null}
      {status === "error" ? (
        <p className="pg-muted pg-google-signin-hint">Google Sign-In could not be loaded. Use username and password.</p>
      ) : null}
      <div ref={hostRef} className="pg-google-signin-host" />
    </div>
  );
}
